/**
 * The display settings behind the toolbar gear (DSGN-63 Phase 1, Figma
 * "Display" frames). The defaults and ranges come from the designer's notes
 * on those frames:
 *
 * - Entity Label: the name under each node icon. Default on.
 * - Edge Label: the relationship text along each line. Default off.
 * - Orphans: nodes with no connection in the graph. Default off (hidden),
 *   never deleted. The design's note said "in the currently filtered graph";
 *   that hid every equipment once its sheet's type was filtered out, so a
 *   filter now dims and never orphans (Slack C0A52ABLSPK, 2026-10-24).
 * - Spacing: how far apart Radial View spreads the graph, 100–300% in 10%
 *   steps, default 100% (the layout as it is drawn today). The design's
 *   frames show 120%, 150% and 190% at the same zoom: the stars and the gaps
 *   between them grow, the nodes and labels keep their size.
 * - Line Thickness: 0–6 in 0.5 steps, default 2.0 (the reference app's
 *   Display panel, where 2.0 sits a third of the way along), applied as the
 *   slider moves. The default draws 1px lines, as thin as the reference
 *   app's: see `linkStrokeWidth`. Node borders, icons and
 *   text are unaffected.
 *
 * Kept beside the popover rather than in it so the component file exports
 * only a component (react-refresh), the same reason `layoutOptions.ts` exists.
 */
export interface DisplaySettings {
  entityLabels: boolean
  edgeLabels: boolean
  orphans: boolean
  /** Percent: 100 is the layout's natural spacing. */
  spacing: number
  lineWidth: number
}

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  entityLabels: true,
  edgeLabels: false,
  orphans: false,
  spacing: 100,
  lineWidth: 2,
}

export const LINE_WIDTH_MIN = 0
export const LINE_WIDTH_MAX = 6
export const LINE_WIDTH_STEP = 0.5

/** The Line Thickness setting as a stroke width: a 0.5px hairline at 0, 1px at the default 2.0, 2px at 6. */
export const linkStrokeWidth = (setting: number): number => 0.5 + setting / 4

export const SPACING_MIN = 100
export const SPACING_MAX = 300
export const SPACING_STEP = 10
